import { useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  pickBestPhoneModelMatch,
  searchPhoneModelsByQuery,
  BASIC_PHONE_DISPLAY_BRANDS,
  hasQualityManufacturerLogo,
  isManufacturerVisibleInPicker,
} from '@redevicer/shared'
import { PHONE_CATALOG, BRAND_ORDER } from '../utils/phoneCatalog'
import { comparePhoneModelRowsNewestFirst } from '../utils/phoneModelSort'
import ManufacturerLogo from './ManufacturerLogo'
import { isMobileWebBrowser } from '../utils/isMobileWebBrowser'
import { keepClickOnTouch } from '../utils/keepClickOnTouch'

const MIN_GLOBAL_SEARCH = 3
const GLOBAL_SEARCH_LIMIT = 30
const MIN_CUSTOM_LABEL = 2
/** First N curated brands shown under “Popular” in the builder manufacturer dropdown. */
const POPULAR_MANUFACTURER_COUNT = 6

interface PhoneModelOpt {
  id: number
  manufacturer: string
  model_name: string
  category?: string | null
  release_year?: number | null
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')

function isJunkModelName(name: string): boolean {
  const n = name.trim()
  if (n.length < 2) return true
  if (/^Q\d+$/i.test(n)) return true
  if (/^wikidata/i.test(n)) return true
  // Accessories, legacy Macs, chips, non-phone Google/Apple products
  if (
    /\b(mouse|buds?|earbud|airpods?|headphone|headset|watch|tv\b|chromecast|nest\b|camera|automotive|titan|authenticator|stadia|controller|appliance|easter|macintosh|mac\s?(pro|mini|book|studio)?|ipod|ipad|power\s*mac|bionic|quicktake|silentype|keyboard|trackpad|homepod|pencil|display|monitor|router|pixelbook|chromebook|doorbell|speaker)\b/i.test(
      n,
    )
  ) {
    return true
  }
  return false
}

/** Curated-brand models must look like real phones (catalog or brand pattern). */
function catalogModelRank(manufacturer: string, modelName: string): number {
  const catalog = PHONE_CATALOG[manufacturer]
  if (!catalog) return 9_000
  const bn = norm(modelName)
  const order = catalog.all.map(norm)
  const exact = order.indexOf(bn)
  if (exact >= 0) return exact
  const fuzzy = order.findIndex((cn) => bn === cn || bn.includes(cn) || cn.includes(bn))
  return fuzzy >= 0 ? fuzzy : 9_000
}

/** Drop accessories / junk rows from the API catalog. */
function isBrowsePhone(m: PhoneModelOpt): boolean {
  return !isJunkModelName(m.model_name)
}

const BRAND_RANK = new Map(BRAND_ORDER.map((b, i) => [b, i]))

interface ListingModelPickerProps {
  models: PhoneModelOpt[]
  /** Selected model id as string ('' = none). */
  value: string
  onChange: (id: string) => void
  loading?: boolean
  placeholder?: string
  /** Prefill model search (e.g. from header search on sell page). */
  seedQuery?: string
  /** When true, do not restrict to curated smartphone brands (basic phones). */
  allowAnyBrand?: boolean
  /** Allow seller-entered phone name when not in catalog (basic-phone flow). */
  allowCustomLabel?: boolean
  onCustomLabel?: (label: string) => void
  customLabelMinLength?: number
  /** Valuation widget: collapsed until user opens picker. */
  collapsible?: boolean
  /** Show placeholder hint below picker (off when form already has a field label). */
  showHint?: boolean
  /** Wide desktop listing builder: compact manufacturer + model fields. */
  variant?: 'default' | 'builder-desktop'
  modelsLoadFailed?: boolean
  onRetryModels?: () => void
}

/**
 * Step-by-step model picker for the sell form.
 * Brand → Model, using backend phone models (keeps phone_model_id).
 * Models sorted by curated catalog order (newest → oldest), then extras.
 */
export default function ListingModelPicker({
  models,
  value,
  onChange,
  loading,
  placeholder,
  seedQuery = '',
  allowAnyBrand = false,
  allowCustomLabel = false,
  onCustomLabel,
  customLabelMinLength = MIN_CUSTOM_LABEL,
  collapsible = false,
  showHint = true,
  variant = 'default',
  modelsLoadFailed = false,
  onRetryModels,
}: ListingModelPickerProps) {
  const { t } = useTranslation()
  const resolvedPlaceholder = placeholder ?? t('listing.modelPickPlaceholder')
  const [brand, setBrand] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [globalQuery, setGlobalQuery] = useState('')
  const [expanded, setExpanded] = useState(!collapsible)
  const [builderPanel, setBuilderPanel] = useState<'brand' | 'model' | null>(null)
  const isBuilderDesktop = variant === 'builder-desktop'

  useEffect(() => {
    const q = seedQuery.trim()
    if (!q || value) return
    setQuery(q)
    const qn = q.toLowerCase()
    const hit = models.find(
      (m) =>
        m.model_name.toLowerCase().includes(qn) ||
        m.manufacturer.toLowerCase().includes(qn) ||
        `${m.manufacturer} ${m.model_name}`.toLowerCase().includes(qn),
    )
    if (hit) setBrand(hit.manufacturer)
  }, [seedQuery, models, value])

  const cleanModels = useMemo(() => models.filter(isBrowsePhone), [models])

  const globalHits = useMemo(() => {
    const q = globalQuery.trim()
    if (q.length < MIN_GLOBAL_SEARCH) return []
    return searchPhoneModelsByQuery(cleanModels, q, GLOBAL_SEARCH_LIMIT)
  }, [cleanModels, globalQuery])

  const showGlobalSearch = globalQuery.trim().length >= MIN_GLOBAL_SEARCH

  const selected = useMemo(
    () => models.find((m) => String(m.id) === value) || null,
    [models, value],
  )

  useEffect(() => {
    if (!isBuilderDesktop || !selected) return
    setBrand(selected.manufacturer)
  }, [isBuilderDesktop, selected])

  const mobileWebPicker = isBuilderDesktop && isMobileWebBrowser()

  useEffect(() => {
    if (!isBuilderDesktop) return
    const open = builderPanel != null
    const scroll = document.querySelector('.builder-panel__scroll')
    const panel = document.querySelector('.listing-form--builder .builder-panel')
    scroll?.classList.toggle('builder-panel__scroll--dropdown-open', open)
    panel?.classList.toggle('builder-panel--dropdown-open', open)
    return () => {
      scroll?.classList.remove('builder-panel__scroll--dropdown-open')
      panel?.classList.remove('builder-panel--dropdown-open')
    }
  }, [isBuilderDesktop, builderPanel])

  const builderDropdownSheetClass = mobileWebPicker ? ' builder-brand-dropdown--inline' : ''
  const manufacturerContainerClass = mobileWebPicker
    ? 'builder-brand-dropdown__list'
    : 'builder-brand-dropdown__grid'

  const brands = useMemo(() => {
    const set = new Set<string>()
    cleanModels.forEach((m) => set.add(m.manufacturer))
    return [...set].sort(
      (a, b) => (BRAND_RANK.get(a) ?? 99) - (BRAND_RANK.get(b) ?? 99) || a.localeCompare(b),
    )
  }, [cleanModels])

  const displayBrands = useMemo(() => {
    const withLogo = (list: string[]) =>
      list.filter((b) => hasQualityManufacturerLogo(b) && isManufacturerVisibleInPicker(b))
    if (allowAnyBrand) {
      const withModels = new Set(cleanModels.map((m) => m.manufacturer))
      return withLogo(BASIC_PHONE_DISPLAY_BRANDS.filter((b) => withModels.has(b)))
    }
    /** All listed manufacturers from the API (BRAND_ORDER ranks popular + sort only). */
    return withLogo(brands)
  }, [allowAnyBrand, cleanModels, brands])

  const popularManufacturerBrands = useMemo(() => {
    const popularOrder = BRAND_ORDER.slice(0, POPULAR_MANUFACTURER_COUNT)
    const inDisplay = new Set(displayBrands)
    return popularOrder.filter((b) => inDisplay.has(b))
  }, [displayBrands])

  const otherManufacturerBrands = useMemo(() => {
    const popular = new Set(BRAND_ORDER.slice(0, POPULAR_MANUFACTURER_COUNT))
    return displayBrands.filter((b) => !popular.has(b))
  }, [displayBrands])

  const brandModels = useMemo(() => {
    if (!brand) return []
    return cleanModels
      .filter((m) => m.manufacturer === brand)
      .sort((a, b) => comparePhoneModelRowsNewestFirst(a, b, catalogModelRank))
  }, [cleanModels, brand])

  const filteredBrandModels = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return brandModels
    return brandModels.filter((m) => m.model_name.toLowerCase().includes(q))
  }, [brandModels, query])

  const bestGlobalMatch = useMemo(() => {
    const q = globalQuery.trim()
    if (q.length < MIN_GLOBAL_SEARCH) return null
    return pickBestPhoneModelMatch(cleanModels, q)
  }, [cleanModels, globalQuery])

  const bestBrandMatch = useMemo(() => {
    const q = query.trim()
    if (!brand || !q) return null
    return pickBestPhoneModelMatch(brandModels, q)
  }, [brand, brandModels, query])

  function pickModel(id: number) {
    onChange(String(id))
    setGlobalQuery('')
    setQuery('')
    if (collapsible) setExpanded(false)
    if (isBuilderDesktop) setBuilderPanel(null)
  }

  function confirmCustomLabel() {
    const label = brand
      ? `${brand} ${query.trim()}`.trim()
      : globalQuery.trim()
    if (label.length < customLabelMinLength || !onCustomLabel) return
    onCustomLabel(label)
    setGlobalQuery('')
    setQuery('')
    if (collapsible) setExpanded(false)
    if (isBuilderDesktop) setBuilderPanel(null)
  }

  function confirmGlobalQuery() {
    if (bestGlobalMatch) pickModel(bestGlobalMatch.id)
    else confirmCustomLabel()
  }

  function confirmBrandQuery() {
    if (bestBrandMatch) pickModel(bestBrandMatch.id)
    else confirmCustomLabel()
  }

  function handleGlobalKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return
    e.preventDefault()
    confirmGlobalQuery()
  }

  function handleBrandKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return
    e.preventDefault()
    confirmBrandQuery()
  }

  const customGlobalLabel = brand
    ? `${brand} ${query.trim()}`.trim()
    : globalQuery.trim()
  const canUseCustomLabel =
    allowCustomLabel &&
    onCustomLabel != null &&
    customGlobalLabel.length >= customLabelMinLength

  const modelsUnavailable = !loading && models.length === 0

  if (isBuilderDesktop) {
    const activeBrand = selected?.manufacturer ?? brand
    const activeModel = selected?.model_name ?? null

    const brandDropdownOpen = builderPanel === 'brand' && !modelsUnavailable && !loading
    const modelDropdownOpen =
      builderPanel === 'model' && Boolean(activeBrand) && !modelsUnavailable && !loading

    function selectManufacturer(b: string) {
      setBrand(b)
      setQuery('')
      onChange('')
      setBuilderPanel('model')
    }

    function renderModelOption(m: PhoneModelOpt) {
      const isSelected = value === String(m.id)
      return (
        <button
          key={m.id}
          type="button"
          role="option"
          aria-selected={isSelected}
          className={`builder-brand-dropdown__item builder-brand-dropdown__item--model${
            isSelected ? ' builder-brand-dropdown__item--selected' : ''
          }`}
          onMouseDown={keepClickOnTouch}
          onClick={() => pickModel(m.id)}
        >
          <span className="builder-brand-dropdown__name">{m.model_name}</span>
          {isSelected ? <span className="builder-brand-dropdown__check" aria-hidden="true" /> : null}
        </button>
      )
    }

    function renderManufacturerOption(b: string) {
      const isSelected = activeBrand === b
      return (
        <button
          key={b}
          type="button"
          role="option"
          aria-selected={isSelected}
          className={`builder-brand-dropdown__item${
            isSelected ? ' builder-brand-dropdown__item--selected' : ''
          }`}
          onMouseDown={keepClickOnTouch}
          onClick={() => selectManufacturer(b)}
        >
          <span className="builder-brand-dropdown__logo-slot">
            <ManufacturerLogo manufacturer={b} size={28} className="builder-brand-dropdown__logo" />
          </span>
          <span className="builder-brand-dropdown__name">{b}</span>
          {isSelected ? <span className="builder-brand-dropdown__check" aria-hidden="true" /> : null}
        </button>
      )
    }

    const brandDropdownPanel = brandDropdownOpen ? (
      <div
        className={`builder-model-picker__dropdown builder-brand-dropdown${builderDropdownSheetClass}`}
        role="listbox"
        aria-label={t('listing.builder.manufacturerLabel')}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="builder-brand-dropdown__search-wrap">
          <input
            type="search"
            className="builder-brand-dropdown__search"
            placeholder={t('listing.builder.manufacturerSearchPlaceholder')}
            value={globalQuery}
            onChange={(e) => {
              setGlobalQuery(e.target.value)
              if (e.target.value.trim().length >= MIN_GLOBAL_SEARCH) setBrand(null)
            }}
            onKeyDown={handleGlobalKeyDown}
            autoComplete="off"
            aria-label={t('listing.builder.manufacturerSearchPlaceholder')}
          />
        </div>
        <div className="builder-brand-dropdown__scroll">
          {showGlobalSearch ? (
            <div className="builder-brand-dropdown__global-list">
              {globalHits.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="model-picker__row model-picker__row--global"
                  onMouseDown={keepClickOnTouch}
                  onClick={() => {
                    pickModel(m.id)
                    setBuilderPanel('model')
                  }}
                >
                  <span className="model-picker__row-brand">{m.manufacturer}</span>
                  <span>{m.model_name}</span>
                </button>
              ))}
            </div>
          ) : (
            <>
              {popularManufacturerBrands.length > 0 ? (
                <section className="builder-brand-dropdown__section-block">
                  <h3 className="builder-brand-dropdown__section-title">
                    {t('listing.builder.manufacturersPopularSection')}
                  </h3>
                  <div className={manufacturerContainerClass}>
                    {popularManufacturerBrands.map((b) => renderManufacturerOption(b))}
                  </div>
                </section>
              ) : null}
              {otherManufacturerBrands.length > 0 ? (
                <section className="builder-brand-dropdown__section-block">
                  <h3 className="builder-brand-dropdown__section-title">
                    {t('listing.builder.manufacturersAllSection')}
                  </h3>
                  <div className={manufacturerContainerClass}>
                    {otherManufacturerBrands.map((b) => renderManufacturerOption(b))}
                  </div>
                </section>
              ) : null}
            </>
          )}
        </div>
      </div>
    ) : null

    const modelDropdownPanel = modelDropdownOpen ? (
      <div
        className={`builder-model-picker__dropdown builder-brand-dropdown${builderDropdownSheetClass}`}
        role="listbox"
        aria-label={t('listing.builder.modelLabel')}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="builder-brand-dropdown__search-wrap">
          <input
            type="search"
            className="builder-brand-dropdown__search"
            placeholder={t('listing.builder.modelSearchPlaceholder', { brand: activeBrand ?? '' })}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleBrandKeyDown}
            autoComplete="off"
            autoFocus={!isMobileWebBrowser()}
            aria-label={t('listing.builder.modelSearchPlaceholder', { brand: activeBrand ?? '' })}
          />
        </div>
        <div className="builder-brand-dropdown__scroll">
          {bestBrandMatch ? (
            <div className="builder-brand-dropdown__grid builder-brand-dropdown__grid--models">
              <button
                type="button"
                className="builder-brand-dropdown__item builder-brand-dropdown__item--model builder-brand-dropdown__item--full builder-brand-dropdown__item--suggest"
                onMouseDown={keepClickOnTouch}
                onClick={() => pickModel(bestBrandMatch.id)}
              >
                <span className="builder-brand-dropdown__name">
                  {t('listing.modelUseTyped', { label: bestBrandMatch.model_name })}
                </span>
              </button>
            </div>
          ) : null}
          {canUseCustomLabel && activeBrand && customGlobalLabel.length >= customLabelMinLength ? (
            <section className="builder-brand-dropdown__section-block builder-brand-dropdown__custom-block">
              <p className="builder-brand-dropdown__custom-hint">
                {t('listing.builder.modelMissingInlineHint')}
              </p>
              <div className="builder-brand-dropdown__grid builder-brand-dropdown__grid--models">
                <button
                  type="button"
                  className="builder-brand-dropdown__item builder-brand-dropdown__item--model builder-brand-dropdown__item--full builder-brand-dropdown__item--custom"
                  onMouseDown={keepClickOnTouch}
                  onClick={confirmCustomLabel}
                >
                  <span className="builder-brand-dropdown__name">
                    {t('listing.modelUseCustom', { label: customGlobalLabel })}
                  </span>
                </button>
              </div>
            </section>
          ) : null}
          {filteredBrandModels.length > 0 ? (
            <section className="builder-brand-dropdown__section-block">
              <h3 className="builder-brand-dropdown__section-title">
                {t('listing.builder.modelsAllSection')}
              </h3>
              <div className="builder-brand-dropdown__grid builder-brand-dropdown__grid--models">
                {filteredBrandModels.map((m) => renderModelOption(m))}
              </div>
            </section>
          ) : canUseCustomLabel &&
            activeBrand &&
            customGlobalLabel.length >= customLabelMinLength ? null : (
            <p className="builder-model-dropdown__empty">{t('listing.builder.modelsEmpty')}</p>
          )}
        </div>
      </div>
    ) : null

    return (
      <div className={`builder-model-picker${builderPanel ? ' builder-model-picker--open' : ''}`}>
        <div className="builder-model-row">
          <div className="builder-model-field-wrap">
            <button
              type="button"
              className={`builder-model-field builder-model-field--brand${
                builderPanel === 'brand' ? ' builder-model-field--open' : ''
              }${loading ? ' builder-model-field--loading' : ''}`}
              aria-expanded={builderPanel === 'brand'}
              aria-haspopup="listbox"
              disabled={loading}
              onClick={() => setBuilderPanel((p) => (p === 'brand' ? null : 'brand'))}
            >
              <span className="builder-model-field__label">{t('listing.builder.manufacturerLabel')}</span>
              <span className="builder-model-field__value">
                {activeBrand ? (
                  <span className="builder-model-field__brand">
                    {hasQualityManufacturerLogo(activeBrand) ? (
                      <ManufacturerLogo manufacturer={activeBrand} size={22} className="builder-model-field__logo" />
                    ) : null}
                    <span className="builder-model-field__text">{activeBrand}</span>
                  </span>
                ) : (
                  <span className="builder-model-field__placeholder">
                    {t('listing.builder.manufacturerPlaceholder')}
                  </span>
                )}
              </span>
              <span className="builder-model-field__chev" aria-hidden="true" />
            </button>
            {brandDropdownPanel}
          </div>

          <div className="builder-model-field-wrap">
            <button
              type="button"
              className={`builder-model-field${builderPanel === 'model' ? ' builder-model-field--open' : ''}${
                !activeBrand || loading || modelsUnavailable ? ' builder-model-field--disabled' : ''
              }`}
              aria-expanded={builderPanel === 'model'}
              aria-haspopup="listbox"
              disabled={!activeBrand || loading || modelsUnavailable}
              onClick={() => {
                if (!activeBrand || loading || modelsUnavailable) return
                setBuilderPanel((p) => (p === 'model' ? null : 'model'))
              }}
            >
              <span className="builder-model-field__label">{t('listing.builder.modelLabel')}</span>
              <span className="builder-model-field__value">
                {activeModel ? (
                  <span className="builder-model-field__text">{activeModel}</span>
                ) : (
                  <span className="builder-model-field__placeholder">
                    {t('listing.builder.modelPlaceholder')}
                  </span>
                )}
              </span>
              <span className="builder-model-field__chev" aria-hidden="true" />
            </button>
            {modelDropdownPanel}
          </div>
        </div>

        {loading ? (
          <p className="builder-model-picker__status meta-text" role="status">
            {t('listing.modelsLoading')}
          </p>
        ) : null}

        {modelsUnavailable ? (
          <div className="builder-model-picker__error" role="alert">
            <p className="meta-text">
              {modelsLoadFailed
                ? t('listing.builder.modelsLoadFailed')
                : t('listing.builder.modelsEmpty')}
            </p>
            {onRetryModels ? (
              <button type="button" className="builder-model-picker__retry" onClick={onRetryModels}>
                {t('listing.builder.modelsRetry')}
              </button>
            ) : null}
          </div>
        ) : null}

      </div>
    )
  }

  if (loading) {
    return <p className="meta-text listing-models-loading">{t('listing.modelsLoading')}</p>
  }

  if (models.length === 0) {
    return (
      <div className="listing-models-empty">
        <p className="meta-text listing-models-loading">
          {modelsLoadFailed
            ? t('listing.builder.modelsLoadFailed')
            : t('listing.builder.modelsEmpty')}
        </p>
        {onRetryModels ? (
          <button type="button" className="builder-model-picker__retry" onClick={onRetryModels}>
            {t('listing.builder.modelsRetry')}
          </button>
        ) : null}
      </div>
    )
  }

  if (selected) {
    return (
      <div className="model-picker model-picker--selected">
        <div className="model-picker__chosen">
          <div className="model-picker__chosen-info">
            <span className="model-picker__chosen-brand">{selected.manufacturer}</span>
            <span className="model-picker__chosen-name">{selected.model_name}</span>
          </div>
          <button
            type="button"
            className="model-picker__change"
            onMouseDown={keepClickOnTouch}
            onClick={() => {
              onChange('')
              setBrand(selected.manufacturer)
              setQuery('')
              if (collapsible) setExpanded(true)
            }}
          >
            Keisti
          </button>
        </div>
      </div>
    )
  }

  if (collapsible && !expanded) {
    return (
      <button
        type="button"
        className="model-picker__collapsed"
        onMouseDown={keepClickOnTouch}
        onClick={() => setExpanded(true)}
      >
        <span>{resolvedPlaceholder}</span>
        <span className="model-picker__arrow">›</span>
      </button>
    )
  }

  return (
    <div className="model-picker">
      {collapsible ? (
        <button
          type="button"
          className="model-picker__collapse"
          onMouseDown={keepClickOnTouch}
          onClick={() => setExpanded(false)}
        >
          ▴ Mažiau
        </button>
      ) : null}

      <input
        type="search"
        className="model-picker__search model-picker__search--global"
        placeholder={t('listing.modelGlobalSearchPlaceholder')}
        value={globalQuery}
        onChange={(e) => {
          setGlobalQuery(e.target.value)
          if (e.target.value.trim().length >= MIN_GLOBAL_SEARCH) setBrand(null)
        }}
        onKeyDown={handleGlobalKeyDown}
        autoComplete="off"
      />
      {globalQuery.trim().length > 0 && globalQuery.trim().length < MIN_GLOBAL_SEARCH ? (
        <p className="model-picker__search-hint">{t('listing.modelGlobalSearchHint')}</p>
      ) : null}

      {!showGlobalSearch && canUseCustomLabel && !brand ? (
        <button
          type="button"
          className="model-picker__row model-picker__row--use-custom"
          onMouseDown={keepClickOnTouch}
          onClick={confirmCustomLabel}
        >
          {t('listing.modelUseCustom', { label: customGlobalLabel })}
        </button>
      ) : null}

      {showGlobalSearch ? (
        <>
          {bestGlobalMatch ? (
            <button
              type="button"
              className="model-picker__row model-picker__row--use-typed"
              onMouseDown={keepClickOnTouch}
              onClick={() => pickModel(bestGlobalMatch.id)}
            >
              {t('listing.modelUseTyped', {
                label: `${bestGlobalMatch.manufacturer} ${bestGlobalMatch.model_name}`,
              })}
            </button>
          ) : null}
          {canUseCustomLabel ? (
            <>
              <p className="model-picker__custom-hint">{t('listing.builder.modelMissingInlineHint')}</p>
              <button
                type="button"
                className="model-picker__row model-picker__row--use-custom"
                onMouseDown={keepClickOnTouch}
                onClick={confirmCustomLabel}
              >
                {t('listing.modelUseCustom', { label: customGlobalLabel })}
              </button>
            </>
          ) : null}
          {globalHits.length === 0 ? (
            bestGlobalMatch || canUseCustomLabel ? null : (
              <p className="model-picker__empty">{t('listing.modelGlobalSearchEmpty')}</p>
            )
          ) : (
            <div className="model-picker__list">
              {globalHits.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="model-picker__row model-picker__row--global"
                  onMouseDown={keepClickOnTouch}
                  onClick={() => pickModel(m.id)}
                >
                  <span className="model-picker__row-brand">{m.manufacturer}</span>
                  <span>{m.model_name}</span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="model-picker__crumbs">
            <button
              type="button"
              className={`model-picker__crumb${!brand ? ' model-picker__crumb--active' : ''}`}
              onMouseDown={keepClickOnTouch}
              onClick={() => { setBrand(null); setQuery('') }}
            >
              {t('listing.builder.manufacturerStep')}
            </button>
            <span className="model-picker__sep">›</span>
            <span className={`model-picker__crumb${brand ? ' model-picker__crumb--active' : ' model-picker__crumb--muted'}`}>
              {brand || t('listing.builder.modelStep')}
            </span>
          </div>

          {!brand ? (
            <>
            {allowAnyBrand && displayBrands.length > 8 ? (
              <p className="model-picker__brand-hint">{t('listing.basicPhoneBrandHint', { count: displayBrands.length })}</p>
            ) : null}
            <div
              className={`model-picker__grid model-picker__grid--brands model-picker__grid--scroll${allowAnyBrand ? ' model-picker__grid--basic-brands' : ''}`}
            >
              {displayBrands.map((b) => (
                  <button
                    key={b}
                    type="button"
                    className="model-picker__brand-tile"
                    onMouseDown={keepClickOnTouch}
                    onClick={() => { setBrand(b); setQuery('') }}
                  >
                    <span className="model-picker__brand-visual">
                      <ManufacturerLogo manufacturer={b} size={allowAnyBrand ? 48 : 40} />
                    </span>
                    <span className="model-picker__brand-label">{b}</span>
                  </button>
              ))}
            </div>
            </>
          ) : (
            <>
              <input
                type="search"
                className="model-picker__search"
                placeholder={t('listing.builder.modelSearchPlaceholder', { brand })}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleBrandKeyDown}
                autoFocus={!isMobileWebBrowser()}
              />
              {bestBrandMatch ? (
                <button
                  type="button"
                  className="model-picker__row model-picker__row--use-typed"
                  onMouseDown={keepClickOnTouch}
                  onClick={() => pickModel(bestBrandMatch.id)}
                >
                  {t('listing.modelUseTyped', {
                    label: bestBrandMatch.model_name,
                  })}
                </button>
              ) : null}
              {canUseCustomLabel && brand && customGlobalLabel.length >= customLabelMinLength ? (
                <>
                  <p className="model-picker__custom-hint">{t('listing.builder.modelMissingInlineHint')}</p>
                  <button
                    type="button"
                    className="model-picker__row model-picker__row--use-custom"
                    onMouseDown={keepClickOnTouch}
                    onClick={confirmCustomLabel}
                  >
                    {t('listing.modelUseCustom', { label: customGlobalLabel })}
                  </button>
                </>
              ) : null}
              <div className="model-picker__list">
                {filteredBrandModels.length === 0 ? (
                  bestBrandMatch || (canUseCustomLabel && brand) ? null : (
                    <p className="model-picker__empty">{t('listing.modelsEmptyShort')}</p>
                  )
                ) : (
                  filteredBrandModels.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className="model-picker__row"
                      onMouseDown={keepClickOnTouch}
                      onClick={() => pickModel(m.id)}
                    >
                      {m.model_name}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </>
      )}
      {showHint && !value ? <span className="model-picker__hint">{resolvedPlaceholder}</span> : null}
    </div>
  )
}
